/// <amd-module name="Artifacts/Package/components/package-details-common/PackageUpstreamSource" />
import * as PackageRes from "Artifacts/Package/Resources";
import { Protocol } from "Artifacts/Root/Constants";
import * as RootRes from "Artifacts/Root/Resources";
import { UpstreamSourceIdentifier } from "Artifacts/Root/types/AzureArtifacts";
import { IMinimalPackageVersion } from "Artifacts/Root/types/IMinimalPackageVersion";
import { IPackage } from "Artifacts/Root/types/IPackage";
import { UpstreamSourceType } from "Artifacts/Root/web-apis/contracts/Feed";
import * as React from "react";
import * as StringUtils from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export interface IPackageUpstreamSourceProps {
  pageContext: IVssPageContext;
  packageSummary: IPackage;
  packageVersion: IMinimalPackageVersion;
  upstreamSources?: UpstreamSourceIdentifier[];
}

export class PackageUpstreamSource extends React.PureComponent<IPackageUpstreamSourceProps> {
  public render(): JSX.Element {
    return this._getUpstreamSourceElement();
  }

  private _getUpstreamSourceElement(): JSX.Element {
    const localSourceName = this._packageToLocalUpstreamSourceName();
    if (localSourceName != null) {
      return (
        <span
          className="upstream-source-grid-cell"
          aria-label={PackageRes.AriaLabel_PackageListItem_Upstream}
        >
          {localSourceName}
        </span>
      );
    }

    const upstreamSource = this._packageToUpstreamSource();
    if (upstreamSource == null) {
      return (
        <span
          className="upstream-source-grid-cell"
          aria-label={PackageRes.AriaLabel_PackageListItem_Upstream}
        >
          {RootRes.FeedSources_Unknown}
        </span>
      );
    }

    if (upstreamSource.upstreamSourceType === UpstreamSourceType.Public) {
      return (
        <span
          className="upstream-source-grid-cell"
          aria-label={PackageRes.AriaLabel_PackageListItem_Upstream}
        >
          {upstreamSource.name}
        </span>
      );
    }

    // Decided to rip out the option to link to the upstream source. We will display the name but nothing else.
    // Future TODO: Have a redirect to the upstream source. See notes in oneNote.
    return (
      <span
        className="upstream-source-grid-cell"
        aria-label={PackageRes.AriaLabel_PackageListItem_Upstream}
      >
        {upstreamSource.name}
      </span>
    );
  }

  private _packageToLocalUpstreamSourceName(): string | null {
    if (
      this._isUpstreamSourceDisplayEnabledForProtocol(this.props.packageSummary.protocol) ===
        false ||
      this.props.packageVersion.directUpstreamSourceId == null
    ) {
      return RootRes.FeedSources_Local;
    }

    return null;
  }

  private _packageToUpstreamSource(): UpstreamSourceIdentifier | null {
    if (
      this._isUpstreamSourceDisplayEnabledForProtocol(this.props.packageSummary.protocol) === false
    ) {
      return null;
    }

    let index = -1;
    if (this.props.packageVersion.directUpstreamSourceId == null) {
      return null;
    }

    if (this.props.upstreamSources == null || this.props.upstreamSources.length === 0) {
      return null;
    }

    index = this.props.upstreamSources.findIndex((upstreamSource: UpstreamSourceIdentifier) => {
      if (this.props.packageVersion.directUpstreamSourceId) {
        return StringUtils.equals(
          upstreamSource.id,
          this.props.packageVersion.directUpstreamSourceId,
          true,
        );
      }
    });

    if (index > -1) {
      return this.props.upstreamSources[index];
    }

    return null;
  }

  private _isUpstreamSourceDisplayEnabledForProtocol(protocolType: string): boolean {
    const isUpackSameCollectionUpstreamAvailable = isFeatureFlagEnabled(
      this.props.pageContext,
      "Packaging.Feed.UPackSameCollectionUpstreams",
      false,
    );

    return (
      (isUpackSameCollectionUpstreamAvailable &&
        StringUtils.equals(protocolType, Protocol.UPack, true) === true) ||
      StringUtils.equals(protocolType, Protocol.Nuget, true) === true ||
      StringUtils.equals(protocolType, Protocol.Npm, true) === true ||
      StringUtils.equals(protocolType, Protocol.Pypi, true) === true ||
      StringUtils.equals(protocolType, Protocol.Maven, true) === true ||
      StringUtils.equals(protocolType, Protocol.Cargo, true)
    );
  }
}
